<template>
  <div class="position-relative">
    <div v-if="message.secondsVisible > -1" class="bar">
      <div class="filled" :style="`background-color: var(--${message.type}); width: ${timerWidth}`"></div>
    </div>

    <div class="alert alert-dismissible fade show" :class="`alert-${message.type}`" role="alert">
      <h6 class="alert-heading">
        <strong>{{ message.title }}</strong>
        <small v-if="message.secondsVisible === -1" class="time float-right">{{ elapsedTime }}</small>
      </h6>
      <button type="button" class="close" aria-label="Close" @click="emit('remove')">
        <span aria-hidden="true">&times;</span>
      </button>

      <template v-if="message.text">
        <div
          ref="messageBody"
          class="message-body"
          :class="[compressed ? 'compressed' : 'expanded']"
          :style="`max-height: min(75vh, ${currentMessageBodyMaxHeight}px)`"
        >
          <!-- {{ message.text }} -->
          <span v-html="message.text" />
        </div>
        <div v-if="textIsLong" class="expand" @click.stop="compressed = !compressed">
          <i class="fa" :class="[compressed ? 'fa-angle-up' : 'fa-angle-down']" />
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
import Message from '@/models/Message'
import { computed, onMounted, onUnmounted, ref } from 'vue'

const longTextHeightBreakpoint = 16 * 4

const props = defineProps<{
  message: Message
  messageIndex: number
}>()

const emit = defineEmits(['remove'])

const interval1 = ref<number>()
const interval2 = ref<number>()
const timerWidth = ref('0')
const elapsedTime = ref('')

const compressed = ref(true)
const messageBody = ref<HTMLElement>()

const textIsLong = computed(() => messageBody.value && messageBody.value.scrollHeight > longTextHeightBreakpoint)
const currentMessageBodyMaxHeight = computed(() => (compressed.value ? longTextHeightBreakpoint : messageBody.value?.scrollHeight))

onMounted(() => {
  interval1.value = window.setInterval(() => {
    elapsedTime.value = getElapsedTime()
  }, 1000 * 60)

  if (props.message.secondsVisible > -1) {
    interval2.value = window.setInterval(() => {
      timerWidth.value = getTimerWidth()
    }, 20)
  }
})

onUnmounted(() => {
  clearInterval(interval1.value)
  clearInterval(interval2.value)
})

const getTimerWidth = () => {
  const now = Date.now()
  const elapsed = (now - props.message.time.getTime()) / 1000
  return 100 - Math.min(1000, (elapsed / props.message.secondsVisible) * 100) + '%'
}
const getElapsedTime = () => {
  let difference = (Date.now() - props.message.time.getTime()) / 1000
  let unit

  interface Unit {
    unit: {
      plural: string
      singular: string
    }
    divisor: number
  }

  const timeUnits: Unit[] = [
    // { unit: { plural: 'sekunder', singular: 'sekund' }, divisor: 1000 },
    {
      unit: {
        plural: 'minuter',
        singular: 'minut',
      },
      divisor: 60,
    },
    {
      unit: {
        plural: 'timmar',
        singular: 'timme',
      },
      divisor: 60,
    },
    {
      unit: {
        plural: 'dagar',
        singular: 'dag',
      },
      divisor: 24,
    },
  ]

  for (let i = 0; i < timeUnits.length; i++) {
    const calc = difference / timeUnits[i].divisor
    if (calc < 1) {
      break
    }
    difference = calc
    unit = timeUnits[i].unit
  }

  const value = Math.round(difference)
  return unit ? `${value} ${value > 1 ? unit.plural : unit.singular} sen` : 'nu'
}
</script>

<style lang="scss" scoped>
.close {
  font-size: 2em;
  outline: none;
}

hr {
  margin: 0.5rem 0;
}

.message-body {
  transition: 0.3s max-height;
  white-space: pre-line;
  overflow: hidden;
  overscroll-behavior: contain;

  &::-webkit-scrollbar {
    width: 0px;
  }

  &.expanded {
    overflow-y: auto;
  }
}

.expand {
  width: 100%;
  font-size: 1.5em;
  margin-bottom: -0.5em;
  line-height: 0;
  text-align: center;
  cursor: pointer;
  color: #212529;
}

.bar {
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;

  .filled {
    height: 3px;
  }
}
</style>
